import { useTranslation } from 'react-i18next';
import { Brain, Shield, Package, Route } from 'lucide-react';
import { Reveal, RevealGroup, RevealItem } from './Reveal';

export default function Features() {
  const { t } = useTranslation();

  return (
    <section id="features" className="py-24 md:py-32 bg-[#f0eee6] dark:bg-[#1a1918]">
      <div className="mx-auto max-w-6xl px-6">
        {/* Section header */}
        <Reveal className="mb-14 text-center">
          <span className="eyebrow inline-flex items-center gap-2 rounded-full border border-[#1414131a] dark:border-[#faf9f51a] px-4 py-1.5 text-[#6b6a68] dark:text-[#9a9893]">
            <span className="h-1.5 w-1.5 rounded-full bg-[#ae5630]" />
            {t('features.eyebrow', '核心能力')}
          </span>
          <h2 className="font-display display-tight mt-5 text-3xl font-medium leading-[1.1] text-[#141413] dark:text-[#faf9f5] md:text-5xl">
            {t('features.title')}
          </h2>
        </Reveal>

        {/*
          非对称错落网格：
          行1 — memory（双层 bezel 大卡，宽 7/12）+ privacy（单层卡，宽 5/12）
          行2 — skills（单层卡，宽 5/12）+ extensible（双层 bezel 大卡，宽 7/12）
        */}
        <RevealGroup className="grid grid-cols-1 gap-4 md:grid-cols-12">

          {/* memory — 主卡，双层 bezel，跨 7 列 */}
          <RevealItem className="md:col-span-7">
            <div className="h-full rounded-[1.5rem] border border-[#1414131a] bg-[#1414130a] p-1.5 shadow-warm dark:border-[#faf9f51a] dark:bg-[#faf9f50a]">
              <div className="group flex h-full flex-col rounded-[calc(1.5rem-0.375rem)] border border-[#1414130d] bg-[#fffefb] px-8 py-8 shadow-inner-glow transition-all duration-300 ease-spring dark:border-[#faf9f50d] dark:bg-[#1a1918]">
                <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-[#ae5630]/10 text-[#ae5630] transition-all duration-300 ease-spring group-hover:bg-[#ae5630]/20 dark:bg-[#ae5630]/15 dark:group-hover:bg-[#ae5630]/25">
                  <Brain size={26} aria-hidden="true" />
                </div>
                <h3 className="font-display display-tight text-2xl font-medium leading-[1.15] text-[#141413] dark:text-[#faf9f5] md:text-3xl">
                  {t('features.memory.title')}
                </h3>
                <p className="mt-4 max-w-sm text-base leading-relaxed text-[#6b6a68] dark:text-[#9a9893]">
                  {t('features.memory.desc')}
                </p>
                <div className="mt-8 h-px w-full bg-gradient-to-r from-[#ae5630]/30 via-[#ae5630]/10 to-transparent" />
              </div>
            </div>
          </RevealItem>

          {/* privacy — 单层卡，跨 5 列 */}
          <RevealItem className="md:col-span-5">
            <div className="group h-full rounded-2xl border border-[#1414131a] bg-[#fffefb] p-7 shadow-warm transition-all duration-300 ease-spring hover:border-[#ae5630]/30 dark:border-[#faf9f51a] dark:bg-[#1a1918] dark:hover:border-[#ae5630]/30">
              <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-xl bg-[#ae5630]/10 text-[#ae5630] transition-all duration-300 ease-spring group-hover:bg-[#ae5630]/20 dark:bg-[#ae5630]/15 dark:group-hover:bg-[#ae5630]/25">
                <Shield size={22} aria-hidden="true" />
              </div>
              <h3 className="font-display display-tight text-xl font-medium text-[#141413] dark:text-[#faf9f5]">
                {t('features.privacy.title')}
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-[#6b6a68] dark:text-[#9a9893]">
                {t('features.privacy.desc')}
              </p>
            </div>
          </RevealItem>

          {/* skills — 单层卡，跨 5 列（第二行左侧窄） */}
          <RevealItem className="md:col-span-5">
            <div className="group h-full rounded-2xl border border-[#1414131a] bg-[#fffefb] p-7 shadow-warm transition-all duration-300 ease-spring hover:border-[#ae5630]/30 dark:border-[#faf9f51a] dark:bg-[#1a1918] dark:hover:border-[#ae5630]/30">
              <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-xl bg-[#ae5630]/10 text-[#ae5630] transition-all duration-300 ease-spring group-hover:bg-[#ae5630]/20 dark:bg-[#ae5630]/15 dark:group-hover:bg-[#ae5630]/25">
                <Package size={22} aria-hidden="true" />
              </div>
              <h3 className="font-display display-tight text-xl font-medium text-[#141413] dark:text-[#faf9f5]">
                {t('features.skills.title')}
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-[#6b6a68] dark:text-[#9a9893]">
                {t('features.skills.desc')}
              </p>
            </div>
          </RevealItem>

          {/* extensible — 主卡，双层 bezel，跨 7 列（第二行右侧宽） */}
          <RevealItem className="md:col-span-7">
            <div className="h-full rounded-[1.5rem] border border-[#1414131a] bg-[#1414130a] p-1.5 shadow-warm dark:border-[#faf9f51a] dark:bg-[#faf9f50a]">
              <div className="group flex h-full flex-col rounded-[calc(1.5rem-0.375rem)] border border-[#1414130d] bg-[#fffefb] px-8 py-8 shadow-inner-glow transition-all duration-300 ease-spring dark:border-[#faf9f50d] dark:bg-[#1a1918]">
                <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-[#ae5630]/10 text-[#ae5630] transition-all duration-300 ease-spring group-hover:bg-[#ae5630]/20 dark:bg-[#ae5630]/15 dark:group-hover:bg-[#ae5630]/25">
                  <Route size={26} aria-hidden="true" />
                </div>
                <h3 className="font-display display-tight text-2xl font-medium leading-[1.15] text-[#141413] dark:text-[#faf9f5] md:text-3xl">
                  {t('features.extensible.title')}
                </h3>
                <p className="mt-4 max-w-sm text-base leading-relaxed text-[#6b6a68] dark:text-[#9a9893]">
                  {t('features.extensible.desc')}
                </p>
                <div className="mt-8 h-px w-full bg-gradient-to-r from-[#ae5630]/30 via-[#ae5630]/10 to-transparent" />
              </div>
            </div>
          </RevealItem>

        </RevealGroup>
      </div>
    </section>
  );
}
